import { type AuthenticationResult, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { theme } from '@servicedesk/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { msalInstance } from './auth/msal';
import { routerBasename } from './config';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={routerBasename}>{children}</BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (!container) throw new Error('Root element not found');
  const root = createRoot(container);

  // Local dev mode: no Entra configured → render directly (backend dev bypass).
  if (!msalInstance) {
    root.render(
      <StrictMode>
        <Providers>
          <App />
        </Providers>
      </StrictMode>,
    );
    return;
  }

  const msal = msalInstance;
  await msal.initialize();
  await msal.handleRedirectPromise();
  const active = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (active) msal.setActiveAccount(active);
  msal.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const payload = event.payload as AuthenticationResult | null;
      if (payload?.account) msal.setActiveAccount(payload.account);
    }
  });

  root.render(
    <StrictMode>
      <MsalProvider instance={msal}>
        <Providers>
          <App />
        </Providers>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap();
